import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  api,
  formatApiErrorDetail,
  downloadSessionAttendance,
} from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Check,
  X,
  Save,
  CheckCircle2,
  ArrowLeft,
  MapPin,
  Hash,
  Download,
  AlertTriangle,
  LayoutList,
  LayoutGrid,
  Loader2,
  Search,
} from "lucide-react";
import moment from "moment";
import { toast } from "sonner";
import { SessionPageSkeleton } from "@/components/skeletons/PageSkeletons";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const STATUSES = [
  {
    key: "present",
    label: "Present",
    cls: "bg-[#D1FAE5] text-[#059669] border-[#A7F3D0]",
    icon: Check,
  },
  {
    key: "absent",
    label: "Absent",
    cls: "bg-[#FFE4E6] text-[#E11D48] border-[#FECDD3]",
    icon: X,
  },
];

function StudentAvatar({ student, isAnomaly, size = "md" }) {
  const sizeCls = size === "lg" ? "w-14 h-14 text-base" : "w-10 h-10 text-sm";
  return (
    <div
      className={`${sizeCls} rounded-full flex items-center justify-center font-bold shrink-0 ${isAnomaly ? "bg-[#EDE9FE] text-[#7C3AED]" : "bg-[#FDECEF] text-[#C8102E]"}`}
    >
      {isAnomaly ? (
        <AlertTriangle className="w-4 h-4" />
      ) : (
        student.name
          .split(" ")
          .map((x) => x[0])
          .slice(0, 2)
          .join("")
      )}
    </div>
  );
}

function CameraTag({ camera }) {
  if (!camera) return null;
  const label = camera === "both" ? "Both" : camera === "dome" ? "Dome" : "PTZ";
  return (
    <span className="inline-flex items-center shrink-0 whitespace-nowrap px-1.5 py-0.5 rounded bg-[#F1F5F9] text-[#475569] font-semibold uppercase tracking-wide">
      {label}
    </span>
  );
}

/**
 * Capture time from the image filename, e.g.
 * ..._step0001_20261022T020742287295Z.jpg -> 2026-10-22T02:07:42.287Z (UTC).
 * Returns a local-time label, or "" when the name has no timestamp.
 */
function captureTimeLabel(value) {
  if (!value) return "";
  const m = String(value).match(/_(\d{8}T\d+Z)/i);
  if (!m) return "";
  const time = moment.utc(m[1], "YYYYMMDD[T]HHmmssSSSSSS[Z]");
  if (!time.isValid()) return "";
  return time.local().format("DD MMM YYYY, hh:mm:ss a");
}

function captureImageSrc(value) {
  if (!value) return "";
  const s = String(value).trim();
  if (
    s.startsWith("data:image") ||
    s.startsWith("http://") ||
    s.startsWith("https://")
  ) {
    return s;
  }
  if (/^[A-Za-z0-9+/=\r\n]+$/.test(s) && s.length > 64) {
    return `data:image/jpeg;base64,${s.replace(/\s/g, "")}`;
  }
  return "";
}

const CYCLE_THEMES = [
  { bar: "bg-[#C8102E]", wash: "bg-[#FFF7F8]", chip: "bg-[#C8102E]" },
  { bar: "bg-[#C8102E]", wash: "bg-[#FFF7F8]", chip: "bg-[#C8102E]" },
  { bar: "bg-[#C8102E]", wash: "bg-[#FFF7F8]", chip: "bg-[#C8102E]" },
  { bar: "bg-[#C8102E]", wash: "bg-[#FFF7F8]", chip: "bg-[#C8102E]" },
  { bar: "bg-[#C8102E]", wash: "bg-[#FFF7F8]", chip: "bg-[#C8102E]" },
];

function groupDetectionsByCycle(items) {
  const groups = new Map();
  items.forEach((item, index) => {
    const cycle = Number(item.cycle) || 0;
    if (!groups.has(cycle)) groups.set(cycle, []);
    groups.get(cycle).push({ ...item, _index: index });
  });
  return [...groups.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([cycle, list]) => ({
      cycle,
      items: list.sort((a, b) => {
        const presetDiff = (Number(a.preset) || 0) - (Number(b.preset) || 0);
        if (presetDiff !== 0) return presetDiff;
        return (Number(a.step) || 0) - (Number(b.step) || 0);
      }),
    }));
}

function DetectionCyclesSkeleton() {
  return (
    <div className="flex flex-col gap-4" data-testid="detections-skeleton">
      {[6, 4].map((count, card) => (
        <div
          key={card}
          className="overflow-hidden rounded-xl border border-[#E2E8F0] bg-[#F8FAFC]"
        >
          <div className="flex items-center gap-2.5 px-4 py-3">
            <Skeleton className="h-7 w-7 rounded-full bg-[#E2E8F0]" />
            <div className="space-y-1.5">
              <Skeleton className="h-3.5 w-16 bg-[#E2E8F0]" />
              <Skeleton className="h-2.5 w-12 bg-[#E2E8F0]" />
            </div>
          </div>
          <div className="flex flex-wrap gap-2 px-4 pb-4">
            {Array.from({ length: count }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-28 rounded-lg bg-[#E2E8F0]" />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function ViewDetections({ sessionId, student }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [activeKey, setActiveKey] = useState("");
  const [activeCycle, setActiveCycle] = useState(null);
  const [image, setImage] = useState("");
  const [imageError, setImageError] = useState("");
  const [imageLoading, setImageLoading] = useState(false);
  const imageRequest = useRef(null);

  const openModal = () => {
    setOpen(true);
    setItems([]);
    setError("");
    setImage("");
    setImageError("");
    setActiveKey("");
    setActiveCycle(null);
    setLoading(true);
    api
      .get(`/sessions/${sessionId}/detections`, {
        params: { student_id: student.id },
      })
      .then((r) => setItems(r.data?.detections || []))
      .catch((err) =>
        setError(formatApiErrorDetail(err?.response?.data?.detail)),
      )
      .finally(() => setLoading(false));
  };

  const loadImage = (item, key) => {
    imageRequest.current?.abort();
    const controller = new AbortController();
    imageRequest.current = controller;
    setActiveKey(key);
    setActiveCycle(Number(item.cycle) || 0);
    setImage("");
    setImageError("");
    setImageLoading(true);
    api
      .get(`/v2/sessions/${sessionId}/captures/${item.step}/image`, {
        params: { camera: item.camera },
        signal: controller.signal,
      })
      .then((r) => {
        if (imageRequest.current !== controller) return;
        setImage(r.data?.url || "");
      })
      .catch((err) => {
        if (imageRequest.current !== controller || controller.signal.aborted) return;
        setImageError(formatApiErrorDetail(err?.response?.data?.detail));
      })
      .finally(() => {
        if (imageRequest.current !== controller) return;
        setImageLoading(false);
      });
  };

  const src = captureImageSrc(image);
  const capturedAt = captureTimeLabel(image);
  const cycles = groupDetectionsByCycle(items);

  if (student.status === "absent") return null;

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        className="text-[11px] font-semibold text-[#C8102E] hover:underline whitespace-nowrap shrink-0"
        data-testid={`view-detections-${student.id}`}
      >
        View detections
      </button>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) imageRequest.current?.abort();
        }}
      >
        <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{student.name}</DialogTitle>
            {loading ? (
              <Skeleton className="h-4 w-40 bg-[#E2E8F0]" />
            ) : (
              !error && (
                <p className="text-sm text-[#64748B]">
                  {`${items.length} detection${items.length === 1 ? "" : "s"} across ${cycles.length} cycle${cycles.length === 1 ? "" : "s"}`}
                </p>
              )
            )}
          </DialogHeader>
          {loading && <DetectionCyclesSkeleton />}
          {error && <div className="text-sm text-[#E11D48]">{error}</div>}
          {!loading && !error && items.length === 0 && (
            <div className="text-sm text-[#64748B]">
              No detections for this student.
            </div>
          )}
          {cycles.length > 0 && (
            <div className="flex flex-col gap-4">
              {cycles.map(({ cycle, items: steps }) => {
                const theme =
                  CYCLE_THEMES[(Math.max(cycle, 1) - 1) % CYCLE_THEMES.length];
                const showCapture =
                  activeCycle === cycle &&
                  (imageLoading || image || imageError);
                return (
                  <section
                    key={cycle}
                    className={`overflow-hidden rounded-xl border border-[#E2E8F0] ${theme.wash}`}
                  >
                    <div className="flex items-center justify-between px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <span
                          className={`inline-flex h-7 min-w-7 items-center justify-center rounded-full px-2 text-xs font-bold text-white ${theme.chip}`}
                        >
                          {cycle}
                        </span>
                        <div>
                          <div className="text-sm font-semibold text-[#0F172A]">
                            Cycle {cycle}
                          </div>
                          <div className="text-[11px] text-[#64748B]">
                            {steps.length} frame{steps.length === 1 ? "" : "s"}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 px-4 pb-4">
                      {steps.map((item) => {
                        const key = `${item.camera}-${item.step}-${item._index}`;
                        const active = key === activeKey;
                        const camera = item.camera === "dome" ? "Dome" : "PTZ";
                        return (
                          <button
                            key={key}
                            type="button"
                            onClick={() => loadImage(item, key)}
                            className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-left transition-colors ${
                              active
                                ? "border-[#C8102E] bg-white text-[#0F172A] shadow-sm"
                                : "border-white/80 bg-white/70 text-[#334155] hover:bg-white"
                            }`}
                          >
                            <span
                              className={`h-8 w-1 rounded-full ${active ? "bg-[#C8102E]" : theme.bar}`}
                            />
                            <span>
                              <span className="block text-xs font-semibold">
                                Preset {item.preset}
                                <span className="ml-1.5 font-medium text-[#94A3B8]">
                                  {camera}
                                </span>
                              </span>
                              <span className="block text-[11px] text-[#64748B]">
                                Step {item.step}
                              </span>
                            </span>
                            {active && imageLoading && (
                              <Loader2 className="ml-1 h-3.5 w-3.5 shrink-0 animate-spin text-[#C8102E]" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                    {showCapture && (
                      <div className="border-t border-[#E2E8F0] bg-white px-4 py-3">
                        {imageLoading && (
                          <Skeleton className="h-48 w-full rounded-lg bg-[#E2E8F0]" />
                        )}
                        {imageError && (
                          <div className="text-sm text-[#E11D48]">
                            {imageError}
                          </div>
                        )}
                        {src && !imageError && (
                          <img
                            src={src}
                            alt="Detection capture"
                            data-testid="capture-image"
                            onError={() => {
                              setImage("");
                              setImageError(
                                "Capture image could not be loaded. Please try again.",
                              );
                            }}
                            className="w-full max-h-[70vh] object-contain rounded-lg border border-[#E2E8F0] bg-[#F8FAFC]"
                          />
                        )}
                        {src && !imageError && capturedAt && (
                          <div
                            className="mt-2 text-xs text-[#64748B]"
                            data-testid="capture-time"
                          >
                            Captured:{" "}
                            <span className="font-semibold text-[#0F172A]">
                              {capturedAt}
                            </span>
                          </div>
                        )}
                        {image && !src && !imageLoading && (
                          <pre className="text-xs whitespace-pre-wrap break-all text-[#475569]">
                            {image}
                          </pre>
                        )}
                      </div>
                    )}
                  </section>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function StatusControls({ student, sessionClosed, onSetStatus }) {
  const isAnomaly = student.status === "anomaly";
  if (isAnomaly) {
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border bg-[#EDE9FE] text-[#7C3AED] border-[#DDD6FE]">
        Anomaly
      </span>
    );
  }
  return (
    <div className="flex items-center gap-2">
      {STATUSES.map((st) => {
        const Icon = st.icon;
        const active = student.status === st.key;
        return (
          <button
            key={st.key}
            onClick={() => onSetStatus(student.id, st.key)}
            disabled={sessionClosed}
            data-testid={`mark-${st.key}-${student.id}`}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border transition-colors duration-200 ease-in-out disabled:opacity-50 ${
              active
                ? st.cls
                : "border-[#E2E8F0] text-[#475569] hover:bg-[#F1F5F9]"
            }`}
          >
            <Icon className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">{st.label}</span>
          </button>
        );
      })}
    </div>
  );
}

export default function Session() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [closing, setClosing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [startingActivity, setStartingActivity] = useState(false);
  const [viewMode, setViewMode] = useState("list");
  const [includeAnomalies, setIncludeAnomalies] = useState(false);
  const [htnoInput, setHtnoInput] = useState("");
  const [htnoMatchedIds, setHtnoMatchedIds] = useState(null);
  const [searchingHtno, setSearchingHtno] = useState(false);
  const [progress, setProgress] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/sessions/${id}`).then((r) =>
      setData({
        ...r.data,
        // Empty status is not a fourth state. Show Absent selected unless the row is an anomaly.
        students: r.data.students.map((s) =>
          s.status === "anomaly" ? s : { ...s, status: s.status || "absent" },
        ),
      }),
    );
  }, [id]);

  const sessionStatus = data?.session?.status;
  useEffect(() => {
    // Class sessions are "open" while running and "closed" after stop. Poll unless closed.
    if (!sessionStatus || sessionStatus === "closed") return undefined;
    let cancelled = false;
    const load = () => {
      api
        .get(`/sessions/${id}/progress`)
        .then((r) => {
          if (!cancelled) setProgress(r.data);
        })
        .catch(() => {});
    };
    load();
    const timer = setInterval(load, 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [id, sessionStatus]);
  if (!data) return <SessionPageSkeleton />;

  // Search button: BE call GET /sessions/:id?htno=... — only the matched
  // ids are kept, so local status edits / counts / save still use the full roster.
  const searchByHtno = async (e) => {
    e.preventDefault();
    const htno = htnoInput.trim();
    setSearchingHtno(true);
    try {
      const r = await api.get(`/sessions/${id}`, {
        params: htno ? { htno } : {},
      });
      setHtnoMatchedIds(
        htno ? new Set((r.data?.students || []).map((s) => s.id)) : null,
      );
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) || "Search failed",
      );
    } finally {
      setSearchingHtno(false);
    }
  };

  const setStatus = (studentId, status) => {
    setData((prev) => ({
      ...prev,
      students: prev.students.map((s) =>
        s.id === studentId ? { ...s, status } : s,
      ),
    }));
  };
  const bulk = (status) =>
    setData((prev) => ({
      ...prev,
      students: prev.students.map((s) =>
        s.status === "anomaly" ? s : { ...s, status },
      ),
    }));

  const save = async () => {
    setSaving(true);
    try {
      const records = data.students
        .filter((s) => s.status && s.status !== "anomaly")
        .map((s) => ({ student_id: s.id, status: s.status }));
      if (records.length === 0) {
        toast.error("Mark at least one student first");
        setSaving(false);
        return;
      }
      await api.post(`/sessions/${id}/mark`, { records });
      toast.success(`Saved attendance for ${records.length} students`);
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e.response?.data?.detail) || "Save failed",
      );
    } finally {
      setSaving(false);
    }
  };

  const downloadCsv = async () => {
    setDownloading(true);
    try {
      const filename = await downloadSessionAttendance(id);
      toast.success(`Downloaded ${filename}`);
    } catch (e) {
      toast.error(e.message || "Download failed");
    } finally {
      setDownloading(false);
    }
  };

  const stopClass = async () => {
    setClosing(true);
    try {
      await api.post(`/sessions/${id}/close`);
      toast.success("Class stopped");
      navigate("/dashboard");
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e.response?.data?.detail) ||
          "Could not stop class",
      );
    } finally {
      setClosing(false);
    }
  };

  // One-way. Activity mode starts the dome camera and cannot be switched off in the UI.
  const startActivity = async () => {
    setStartingActivity(true);
    try {
      const { data: session } = await api.post(`/sessions/${id}/activity`);
      setData((prev) => ({ ...prev, session }));
      toast.success("Activity mode on");
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e.response?.data?.detail) ||
          "Could not start activity mode",
      );
    } finally {
      setStartingActivity(false);
    }
  };

  const counts = data.students.reduce((acc, s) => {
    if (s.status) acc[s.status] = (acc[s.status] || 0) + 1;
    return acc;
  }, {});
  const rosterStudents = data.students.filter((s) => s.status !== "anomaly");
  const visibleStudents = (
    includeAnomalies ? data.students : rosterStudents
  ).filter((s) => !htnoMatchedIds || htnoMatchedIds.has(s.id));
  const marked = rosterStudents.filter((s) => s.status).length;
  const sessionShortId = String(data.session.id).slice(0, 8);
  const canStopClass = user?.is_elevated_admin === true;
  const sessionClosed = data.session.status === "closed";
  // dome_session_id is set only after a successful start, so a failed click snaps the switch back off.
  const activityOn = Boolean(String(data.session.dome_session_id || "").trim());
  const canSeeCamera =
    user?.is_super_admin === true || user?.is_qtap_admin === true;

  return (
    <div className="min-h-screen">
      <div className="px-6 md:px-12 pt-10 pb-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-sm text-[#475569] hover:text-[#0F172A] mb-4"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="flex items-baseline gap-3 flex-wrap">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#C8102E] bg-[#FDECEF] px-2 py-1 rounded">
            Live session
          </div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#94A3B8]">
            {data.session.date} · {data.session.start_time}–
            {data.session.end_time}
          </div>
          <div
            className="text-xs font-mono text-[#94A3B8] flex items-center gap-1"
            data-testid="session-id"
          >
            <Hash className="w-3 h-3" />
            {sessionShortId}
          </div>
        </div>
        <h1
          className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tighter text-[#0F172A] mt-3"
          data-testid="session-heading"
        >
          {data.session.course_name}
        </h1>
        <div className="text-[#475569] mt-1 flex items-center gap-4 flex-wrap">
          <span className="text-sm">
            {data.session.course_code} · Section {data.session.section_name}
          </span>
          {data.session.classroom_name && (
            <span className="inline-flex items-center gap-1 text-sm">
              <MapPin className="w-3.5 h-3.5" />
              {data.session.classroom_name}
            </span>
          )}
        </div>
        {progress?.percent != null && (
          <div className="mt-4 max-w-sm" data-testid="session-progress">
            <div className="flex items-center justify-between text-xs font-semibold text-[#475569] mb-1.5">
              <span>session progress</span>
              <span>{progress.percent}%</span>
            </div>
            <div className="h-2 rounded-full bg-[#E2E8F0] overflow-hidden">
              <div
                className="h-full rounded-full bg-[#C8102E]"
                style={{ width: `${progress.percent}%` }}
              />
            </div>
          </div>
        )}
      </div>

      <div className="px-6 md:px-12 pb-6">
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-4 flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4 text-sm">
            <div>
              <span className="font-heading text-lg font-extrabold text-[#0F172A]">
                {marked}
              </span>
              <span className="text-[#94A3B8]">
                {" "}
                / {rosterStudents.length} marked
              </span>
            </div>
            <div className="hidden sm:flex items-center gap-2 text-xs font-semibold">
              <span
                className="px-2 py-1 rounded bg-[#D1FAE5] text-[#059669]"
                data-testid="count-present"
              >
                {counts.present || 0} present
              </span>
              <span
                className="px-2 py-1 rounded bg-[#FFE4E6] text-[#E11D48]"
                data-testid="count-absent"
              >
                {counts.absent || 0} absent
              </span>
              {includeAnomalies && (counts.anomaly || 0) > 0 && (
                <span
                  className="px-2 py-1 rounded bg-[#EDE9FE] text-[#7C3AED]"
                  data-testid="count-anomaly"
                >
                  {counts.anomaly} anomaly
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => bulk("present")}
              className="text-xs font-semibold px-3 py-2 rounded-md border border-[#E2E8F0] hover:bg-[#F1F5F9] transition-colors"
              data-testid="mark-all-present"
            >
              All present
            </button>
            <button
              onClick={() => bulk("absent")}
              className="text-xs font-semibold px-3 py-2 rounded-md border border-[#E2E8F0] hover:bg-[#F1F5F9] transition-colors"
              data-testid="mark-all-absent"
            >
              All absent
            </button>
            <button
              onClick={downloadCsv}
              disabled={downloading}
              className="inline-flex items-center gap-2 text-xs font-semibold px-3 py-2 rounded-md border border-[#E2E8F0] hover:bg-[#F1F5F9] transition-colors"
              data-testid="download-attendance-btn"
            >
              <Download className="w-4 h-4" />
              {downloading ? "Downloading…" : "Download Excel"}
            </button>
            {/* Label plus switch in one control, left of Save. Click turns it on and disables it. */}
            {(!sessionClosed || activityOn) && (
              <button
                type="button"
                onClick={startActivity}
                disabled={activityOn || startingActivity || sessionClosed}
                aria-pressed={activityOn || startingActivity}
                className="inline-flex items-center gap-2 text-xs font-semibold px-3 py-2 rounded-md border border-[#E2E8F0] hover:bg-[#F1F5F9] transition-colors disabled:opacity-100 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                data-testid="activity-mode-btn"
              >
                Activity mode
                <span
                  className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${
                    activityOn || startingActivity
                      ? "bg-[#059669]"
                      : "bg-[#E2E8F0]"
                  }`}
                  aria-hidden="true"
                >
                  <span
                    className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${
                      activityOn || startingActivity
                        ? "translate-x-4"
                        : "translate-x-0.5"
                    }`}
                  />
                </span>
              </button>
            )}
            <button
              onClick={save}
              disabled={saving || sessionClosed}
              className="inline-flex items-center gap-2 bg-[#C8102E] hover:bg-[#9F0D24] text-white px-4 py-2 rounded-md text-sm font-semibold transition-colors disabled:opacity-60"
              data-testid="save-attendance-btn"
            >
              <Save className="w-4 h-4" />
              {saving ? "Saving…" : "Save"}
            </button>
            {canStopClass && !sessionClosed && (
              <button
                onClick={stopClass}
                disabled={closing}
                className="inline-flex items-center gap-2 bg-[#059669] hover:bg-[#047857] text-white px-4 py-2 rounded-md text-sm font-semibold transition-colors"
                data-testid="stop-class-btn"
              >
                <CheckCircle2 className="w-4 h-4" />
                {closing ? "Stopping…" : "Stop class"}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="px-6 md:px-12 pb-16">
        <div className="flex flex-wrap items-center justify-end gap-2 mb-4">
          {(counts.anomaly || 0) > 0 && (
            <button
              type="button"
              onClick={() => setIncludeAnomalies((v) => !v)}
              data-testid="include-anomalies-toggle"
              className="text-xs font-semibold px-3 py-2 h-11 rounded-md border border-[#E2E8F0] bg-white hover:bg-[#F1F5F9] transition-colors"
            >
              {includeAnomalies ? "Hide anomalies" : "Show anomalies"}
            </button>
          )}
          <div
            className="inline-flex items-center rounded-md border border-[#E2E8F0] bg-white p-0.5 h-11"
            data-testid="view-toggle"
          >
            <button
              type="button"
              onClick={() => setViewMode("list")}
              aria-pressed={viewMode === "list"}
              data-testid="view-list-btn"
              className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded text-xs font-semibold transition-colors ${
                viewMode === "list"
                  ? "bg-[#F1F5F9] text-[#0F172A]"
                  : "text-[#94A3B8] hover:text-[#0F172A]"
              }`}
            >
              <LayoutList className="w-4 h-4" />
              List
            </button>
            <button
              type="button"
              onClick={() => setViewMode("grid")}
              aria-pressed={viewMode === "grid"}
              data-testid="view-grid-btn"
              className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded text-xs font-semibold transition-colors ${
                viewMode === "grid"
                  ? "bg-[#F1F5F9] text-[#0F172A]"
                  : "text-[#94A3B8] hover:text-[#0F172A]"
              }`}
            >
              <LayoutGrid className="w-4 h-4" />
              Grid
            </button>
          </div>
          <form className="flex items-center gap-2" onSubmit={searchByHtno}>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
              <input
                type="text"
                placeholder="Search by HTNO"
                value={htnoInput}
                onChange={(e) => setHtnoInput(e.target.value)}
                data-testid="htno-search"
                className="w-56 h-11 pl-9 pr-3 rounded-md border border-[#E2E8F0] bg-white text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-offset-2"
              />
            </div>
            <button
              type="submit"
              disabled={searchingHtno}
              data-testid="htno-search-btn"
              className="inline-flex items-center gap-2 h-11 px-4 rounded-md bg-[#C8102E] hover:bg-[#9F0D24] text-white text-sm font-semibold transition-colors disabled:opacity-60"
            >
              <Search className="w-4 h-4" />
              {searchingHtno ? "Searching…" : "Search"}
            </button>
          </form>
        </div>
        {visibleStudents.length === 0 ? (
          <div className="bg-white border border-[#E2E8F0] rounded-lg px-6 py-8 text-center text-[#94A3B8] text-sm" data-testid="htno-search-empty">
            No students match this HTNO.
          </div>
        ) : viewMode === "list" ? (
          <div
            className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden"
            data-testid="students-list"
          >
            {visibleStudents.map((s, i) => {
              const isAnomaly = s.status === "anomaly";
              return (
                <div
                  key={s.id}
                  className={`px-6 py-4 flex items-center gap-4 hover:bg-[#F8FAFC] transition-colors duration-200 ease-in-out ${i > 0 ? "border-t border-[#E2E8F0]" : ""}`}
                  data-testid={`student-row-${s.id}`}
                >
                  <StudentAvatar student={s} isAnomaly={isAnomaly} />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[#0F172A]">{s.name}</div>
                    <div className="text-xs text-[#94A3B8] flex items-center gap-2">
                      Roll {s.roll_no}
                      {canSeeCamera && <CameraTag camera={s.camera} />}
                      {canSeeCamera && (
                        <ViewDetections sessionId={id} student={s} />
                      )}
                    </div>
                    {s.remark && (
                      <div className="text-xs text-[#7C3AED] mt-1">
                        {s.remark}
                      </div>
                    )}
                  </div>
                  <StatusControls
                    student={s}
                    sessionClosed={sessionClosed}
                    onSetStatus={setStatus}
                  />
                </div>
              );
            })}
          </div>
        ) : (
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
            data-testid="students-grid"
          >
            {visibleStudents.map((s) => {
              const isAnomaly = s.status === "anomaly";
              return (
                <div
                  key={s.id}
                  className="bg-white border border-[#E2E8F0] rounded-lg p-4 flex flex-col gap-3 h-full hover:shadow-sm transition-shadow duration-200 ease-in-out"
                  data-testid={`student-card-${s.id}`}
                >
                  <div className="flex items-start gap-3">
                    <StudentAvatar
                      student={s}
                      isAnomaly={isAnomaly}
                      size="lg"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-[#0F172A] leading-snug">
                        {s.name}
                      </div>
                      <div className="text-xs text-[#94A3B8] mt-1 flex flex-wrap items-center gap-2">
                        <span className="whitespace-nowrap">
                          Roll {s.roll_no}
                        </span>
                        {canSeeCamera && <CameraTag camera={s.camera} />}
                      </div>
                      {s.remark && (
                        <div className="text-xs text-[#7C3AED] mt-1">
                          {s.remark}
                        </div>
                      )}
                    </div>
                  </div>
                  {canSeeCamera && s.status !== "absent" && (
                    <div className="flex items-center justify-end">
                      <ViewDetections sessionId={id} student={s} />
                    </div>
                  )}
                  <div className="mt-auto pt-2 border-t border-[#E2E8F0]">
                    <StatusControls
                      student={s}
                      sessionClosed={sessionClosed}
                      onSetStatus={setStatus}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
